import { useState, useEffect, useCallback, useContext } from "react";
import { useFirestoreGeneral, useFirestoreGeneralOrderBy, useFirestoreIn } from "../../firebase/useFirestore";
import Location from "../../hooks/Location";
import { db } from "../../firebase/config";
import describeQueryError from "../../helpers/firestore/describeQueryError";
import { client } from "../../hooks/Client";
import { useHistory } from "react-router-dom";
import firebase from "../../firebase/compat";
import { measurementMethodLabels } from "../../helpers/dataHelpers/measurementMethods";
import useSettings from "../../hooks/Settings";
import MultipleOptionsField from "../../components/events/MultipleOptionsField.jsx";
import SubTargetgroupsEvents from "../../components/events/SubTargetgroupsEvents.jsx";
import { Auth } from "../../StateManagment/Auth";
import { isFlagOff } from '../../helpers/isFlagOn';
import scaleRange from '../../helpers/questionnaire/scaleRange';
import visibleEventFields from '../../helpers/events/visibleEventFields';
import registrationDateHint from '../../helpers/events/registrationDateHint';

// Personas are tenant-private — Firestore rules block reads for anonymous viewers.
// When nobody is signed in we render these blurred placeholders + a privacy notice
// instead of querying (a query would only throw "Missing or insufficient permissions").
const PLACEHOLDER_PERSONAS = [
  { ID: "placeholder-1", Name: "Sanne de Vries" },
  { ID: "placeholder-2", Name: "Daan Jansen" },
  { ID: "placeholder-3", Name: "Lotte Bakker" },
  { ID: "placeholder-4", Name: "Tim Visser" },
  { ID: "placeholder-5", Name: "Emma Smit" },
];

const PersonaPrivacyNotice = () => (
  <p className="persona-privacy-notice">
    Deze namen zijn vervaagd om privacyredenen. Open het formulier in een ingelogde
    browser om de namen te zien.
  </p>
);

const EventOpen = () => {
  // Context
  const [auth] = useContext(Auth);

  // State
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [activityTargetgroups, setActivityTargetgroups] = useState([{ ID: "", Name: "" }]);
  const [activityId, setActivityId] = useState("");
  const [outputParticipantsByKey, setOutputParticipantsByKey] = useState({});

  // Form state buffers (same structure as AddEvent)
  const [datesByFieldId, setDatesByFieldId] = useState({}); // {[fieldId]: 'yyyy-mm-dd'}
  const [outputsArray, setOutputsArray] = useState([]); // [{fieldId, outputId, value}]
  const [responses, setResponses] = useState({}); // {[fieldId]: {type, values:[{value, optionId?}]}}
  const [openResponses, setOpenResponses] = useState({}); // {[fieldId]: {type:'open', values:[{value}]}}
  const [participantsByFieldId, setParticipantsByFieldId] = useState({}); // {[fieldId]: [personaId]}
  const [targetgroupsByFieldId, setTargetgroupsByFieldId] = useState({}); // {[fieldId]: [tgId]}
  const [subTargetgroupsByFieldId, setSubTargetgroupsByFieldId] = useState({}); // {[fieldId]: { [tgId]: [subId] }}

    // Convenience arrays (not strictly needed but mirroring AddEvent)
  const [personasArray, setPersonasArray] = useState([]);
  const [targetgroupsArray, setTargetgroupsArray] = useState([]);

  // Personas: real data only when signed in (tenant-private). Anonymous viewers keep
  // the blurred placeholders — see the gated subscription effect below.
  const [personas, setPersonas] = useState(PLACEHOLDER_PERSONAS);

  // Hooks
  const eventId = Location()[3];
  const history = useHistory();
  const personaOn = useSettings().personasOn;
  const isSignedIn = !!auth?.ID;

  // Firestore data
  const eventForm     = useFirestoreGeneral("EventForms", "ID", eventId || "");
  const eventFields   = useFirestoreGeneralOrderBy("EventFields", "EventId", eventId || "", "Position", "asc");
  const targetgroups =  useFirestoreIn('Stakeholders', 'ID', activityTargetgroups || [{ ID: '', Name: '' }]);
  const outputs       = useFirestoreGeneralOrderBy("Outputs", "ActivityID", activityId || "", "Position", "asc");
  const activities    = useFirestoreGeneral("Activities", "ID", activityId || "");

  // Activity targetgroups -> used to fetch Stakeholders by IDs
  useEffect(() => {
    eventForm && eventForm.forEach((evt) => {
      setTitle(evt.Title || "");
      setDescription(evt.Description || "");
      setActivityId(evt.ActivityID || "");
    });
  }, [eventForm]);

  useEffect(() => {
    activities && activities.forEach((activity) => {
      const tg = activity.Targetgroup;
      setActivityTargetgroups(tg && tg.length > 0 ? tg : [{ ID: "", Name: "" }]);
    });
  }, [activities]);

  // Only subscribe to the tenant's real personas when signed in. Anonymous viewers
  // keep PLACEHOLDER_PERSONAS — Firestore rules deny Personas reads without auth, so
  // querying here would just spam "Missing or insufficient permissions".
  useEffect(() => {
    if (!isSignedIn) {
      setPersonas(PLACEHOLDER_PERSONAS);
      return;
    }
    const unsub = db
      .collection("Personas")
      .where("CompagnyID", "==", client)
      .where("Status", "==", "active")
      .onSnapshot((snap) => {
        const arr = [];
        snap.forEach((doc) => arr.push({ ...doc.data(), docid: doc.id }));
        setPersonas(arr);
      }, (error) => console.error(
        describeQueryError('Personas', [['CompagnyID', '==', client], ['Status', '==', 'active']], error),
        error
      ));
    return () => unsub();
  }, [isSignedIn]);


  // ── Date fields
  // A form can hold several date fields (Startdatum + Einddatum). They are kept
  // per field; the server picks the event's own moment (the first one in form order).

  // ── Handlers (1:1 with AddEvent)
  const dateHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    setDatesByFieldId((prev) => ({ ...prev, [fieldid]: value }));
  };

  const participantsHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    const checked = e.target.checked;

    setParticipantsByFieldId((prev) => {
      const set = new Set(prev[fieldid] || []);
      if (checked) set.add(value);
      else set.delete(value);
      return { ...prev, [fieldid]: Array.from(set) };
    });

    setPersonasArray((prev) => (checked ? [...prev, value] : prev.filter((v) => v !== value)));
  };

  const outputsHandler = (e) => {
    const fieldId = e.target.dataset.fieldid;
    const outputId = e.target.dataset.outputid;
    const value = e.target.value;
    const outputKey = `${fieldId}-${outputId}`;

    setOutputsArray((prev) => {
      const idx = prev.findIndex((o) => o.fieldId === fieldId && o.outputId === outputId);
      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], value };
        return next;
      }
      return [...prev, { fieldId, outputId, value, participantIds: outputParticipantsByKey[outputKey] || [] }];
    });
  };

  const outputParticipantsHandler = (e) => {
    const fieldId = e.target.dataset.fieldid;
    const outputId = e.target.dataset.outputid;
    const personaId = e.target.value;
    const checked = e.target.checked;
    const outputKey = `${fieldId}-${outputId}`;

    setOutputParticipantsByKey((prev) => {
      const set = new Set(prev[outputKey] || []);
      if (checked) set.add(personaId);
      else set.delete(personaId);
      return { ...prev, [outputKey]: Array.from(set) };
    });

    setOutputsArray((prev) => {
      const idx = prev.findIndex((o) => o.fieldId === fieldId && o.outputId === outputId);
      const participantIds = checked
        ? Array.from(new Set([...(prev[idx]?.participantIds || []), personaId]))
        : (prev[idx]?.participantIds || []).filter((id) => id !== personaId);

      if (idx >= 0) {
        const next = [...prev];
        next[idx] = { ...next[idx], participantIds };
        return next;
      }

      return [...prev, { fieldId, outputId, value: "", participantIds }];
    });
  };

  const targetgroupsHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const tgId = e.target.value;
    const checked = e.target.checked;

    setTargetgroupsByFieldId((prev) => {
      const set = new Set(prev[fieldid] || []);
      if (checked) set.add(tgId);
      else set.delete(tgId);
      return { ...prev, [fieldid]: Array.from(set) };
    });

    setTargetgroupsArray((prev) => (checked ? [...prev, tgId] : prev.filter((v) => v !== tgId)));

    // If unchecking a TG, also clear subs for this field & tg
    if (!checked) {
      setSubTargetgroupsByFieldId((prev) => {
        const fieldMap = { ...(prev[fieldid] || {}) };
        if (fieldMap[tgId]) delete fieldMap[tgId];
        return { ...prev, [fieldid]: fieldMap };
      });
    }
  };

  const openHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    setOpenResponses((prev) => ({
      ...prev,
      [fieldid]: { type: "open", values: [{ value }] },
    }));
  };

  const scaleChange = useCallback((field, value) => {
    setResponses((prev) => ({
      ...prev,
      [field.ID]: { type: "scale", values: [{ value }] },
    }));
  }, []);

  const numberHandler = (e) => {
    const fieldid = e.target.dataset.fieldid;
    const value = e.target.value;
    setResponses((prev) => ({
      ...prev,
      [fieldid]: { type: "number", values: [{ value }] },
    }));
  };

  const handleMultipleChange = useCallback((updates) => {
    // updates: { fieldId, type, add?, remove?, replace? }
    setResponses((prev) => {
      const existing = prev[updates.fieldId]?.values || [];
      let nextValues = existing;

      if (updates.replace) {
        nextValues = [{ optionId: updates.replace.optionId, value: updates.replace.value }];
      } else {
        if (updates.add?.length) {
          nextValues = [
            ...existing,
            ...updates.add.filter((a) => !existing.find((x) => x.optionId === a.optionId)),
          ];
        }
        if (updates.remove?.length) {
          const removeIds = new Set(updates.remove.map((r) => r.optionId));
          nextValues = existing.filter((x) => !removeIds.has(x.optionId));
        }
      }

      return {
        ...prev,
        [updates.fieldId]: { type: updates.type, values: nextValues },
      };
    });
  }, []);

  const handleSubTargetToggle = useCallback((fieldId, targetgroupId, subId, checked) => {
    setSubTargetgroupsByFieldId((prev) => {
      const fieldMap = { ...(prev[fieldId] || {}) };
      const set = new Set(fieldMap[targetgroupId] || []);
      if (checked) set.add(subId);
      else set.delete(subId);
      fieldMap[targetgroupId] = Array.from(set);
      return { ...prev, [fieldId]: fieldMap };
    });
  }, []);

  // ── Save
  // Via de Cloud Function saveEventRegistration, niet rechtstreeks in
  // Firestore: de server bepaalt bij welke organisatie en activiteit dit hoort
  // en toetst elk antwoord tegen het formulier (ADR-022).
  // AI-CONTEXT: See docs/pitfalls.md#security-public-page-reads-its-own-gates
  const saveEvent = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError("");

    try {
      await firebase.app().functions("europe-west1").httpsCallable("saveEventRegistration")({
        client,
        eventId,
        buffers: {
          outputsArray,
          responses,
          openResponses,
          datesByFieldId,
          participantsByFieldId,
          targetgroupsByFieldId,
          subTargetgroupsByFieldId,
        },
      });
      setSaved(true);
    } catch (error) {
      console.error("Opslaan van de registratie mislukt:", error);
      setSaveError(error?.message || "Opslaan is mislukt. Probeer het opnieuw.");
    } finally {
      setSaving(false);
    }
  };

  // AI-CONTEXT: See docs/features/output-registratie-per-keer-of-totaal.md (ADR-070)
  // One date per submission; a period total belongs on the period's last day.
  // The hook yields "" while loading, so check for a real list first.
  const asksOutputs = Array.isArray(eventFields) && eventFields.some((field) => field.Type === "outputs");
  const dateHint = asksOutputs ? registrationDateHint(outputs) : null;

  // ── Field renderer (same logic as AddEvent but styled for your current page)
  const renderField = (field) => {
    switch (field.Type) {
      case "date":
        return (
          <div className="event-builder-type-item-container event-builder-type-item-date-container">
            <input type="date" data-fieldid={field.ID} value={datesByFieldId[field.ID] || ""} onChange={dateHandler} />
            {dateHint && <p className="event-date-hint">{dateHint}</p>}
          </div>
        );

      case "participants":
        return (
          <div className="event-builder-type-item-container event-builder-type-item-multiple-container">
            {!isSignedIn && <PersonaPrivacyNotice />}
            {personas &&
              personas.map((persona) => {
                const checked = (participantsByFieldId[field.ID] || []).includes(persona.ID);
                return (
                  <div key={persona.ID} id="add-event-persona-item-container">
                    <input
                      type="checkbox"
                      data-fieldid={field.ID}
                      value={persona.ID}
                      checked={checked}
                      onChange={participantsHandler}
                      disabled={!isSignedIn}
                    />
                    <label className={isSignedIn ? undefined : "persona-name-blurred"}>{persona.Name}</label>
                  </div>
                );
              })}
          </div>
        );

      case "outputs":
        return (
          <div className="event-builder-type-item-container">
            {outputs &&
              outputs.map((output) => {
                const currentOutput = outputsArray.find(o => o.fieldId === field.ID && o.outputId === output.ID);
                const aantalFilled = Number(currentOutput?.value || 0) > 0;
                const showPersonas = personaOn === "flex" && !isFlagOff(output.LinkParticipantsToOutputs);
                // Outputs measured elsewhere (ADR-011) are read-only here.
                const method = output.MeasurementMethod || "event_form";
                const measuredElsewhere = method !== "event_form";

                if (measuredElsewhere) {
                  return (
                    <div key={output.ID} id="add-event-output-item-container">
                      <div className="event-output-row">
                        <p>{output.Title}</p>
                        <p className="event-output-measured-elsewhere">Wordt gemeten via {measurementMethodLabels[method] || method}</p>
                      </div>
                    </div>
                  );
                }

                return (
                  <div key={output.ID} id="add-event-output-item-container">
                    <div className="event-output-row">
                      <p>{output.Title}</p>
                      <input
                        className="event-output-input"
                        type="number"
                        min="0"
                        placeholder={0}
                        data-outputid={output.ID}
                        data-fieldid={field.ID}
                        onChange={outputsHandler}
                      />
                    </div>
                    <p className="event-output-aantal-helper">Vul eerst het totale aantal in.</p>

                    {showPersonas && (
                      <div className={`event-output-personas-section${aantalFilled ? '' : ' gated'}`}>
                        <div className="event-output-personas-heading">
                          <h4>Wie waren aanwezig?</h4>
                          <span className="event-output-personas-pill">Optioneel</span>
                        </div>
                        <p className="event-output-personas-helper">
                          {aantalFilled
                            ? 'Aanvinken verandert het aantal hierboven niet.'
                            : 'Vul eerst het aantal hierboven in om aan te vinken.'}
                        </p>
                        <div className="event-output-personas-container">
                          {!isSignedIn && <PersonaPrivacyNotice />}
                          {personas &&
                            personas.map((persona) => {
                              const outputKey = `${field.ID}-${output.ID}`;
                              const checked = (outputParticipantsByKey[outputKey] || []).includes(persona.ID);

                              return (
                                <div key={`${output.ID}-${persona.ID}`} className="event-output-persona-option">
                                  <input
                                    type="checkbox"
                                    data-fieldid={field.ID}
                                    data-outputid={output.ID}
                                    value={persona.ID}
                                    checked={checked}
                                    disabled={!aantalFilled || !isSignedIn}
                                    onChange={outputParticipantsHandler}
                                  />
                                  <label className={isSignedIn ? undefined : "persona-name-blurred"}>{persona.Name}</label>
                                </div>
                              );
                            })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        );

      case "targetgroups":
        return (
          <div className="event-builder-type-item-container event-builder-type-item-multiple-container">
            {targetgroups &&
              targetgroups.map((tg) => {
                const tgChecked = (targetgroupsByFieldId[field.ID] || []).includes(tg.ID);
                const selectedSubs = subTargetgroupsByFieldId[field.ID]?.[tg.ID] || [];
                return (
                  <div key={tg.ID} id="add-event-targetgroup-item-container">
                    <input
                      type="checkbox"
                      data-fieldid={field.ID}
                      value={tg.ID}
                      checked={tgChecked}
                      onChange={targetgroupsHandler}
                    />
                    <label>{tg.Organisation}</label>

                    <SubTargetgroupsEvents
                      fieldId={field.ID}
                      targetgroupId={tg.ID}
                      selected={selectedSubs}
                      onToggle={handleSubTargetToggle}
                      disabled={!tgChecked}
                    />
                  </div>
                );
              })}
          </div>
        );

      case "open":
        return (
          <div className="event-builder-type-item-container">
            <textarea
              placeholder="Schrijf hier je antwoord"
              data-fieldid={field.ID}
              onChange={openHandler}
            />
          </div>
        );

      case "number":
        return (
          <div className="event-builder-type-item-container">
            <input type="number" placeholder={0} data-fieldid={field.ID} onChange={numberHandler} />
          </div>
        );

      case "scale": {
        const startLabel = field.ReachStartLabel || '';
        const endLabel = field.ReachEndLabel || '';
        // Formuliervelden worden aangemaakt zonder bereik; die tonen bewust 0 t/m 10.
        const values = scaleRange(field, { fallbackStart: 0, fallbackEnd: 10 });
        return (
          <div className="event-builder-type-item-container">
            <div id="scale-container">
              <p>{startLabel}</p>
              {values.map((val) => (
                <div key={`${field.ID}-${val}`} id="question-type-label-container">
                  <input
                    type="radio"
                    name={field.ID}
                    value={val}
                    onChange={() => scaleChange(field, String(val))}
                  />
                  <label htmlFor={`${field.ID}-${val}`}>{val}</label>
                </div>
              ))}
              <p>{endLabel}</p>
            </div>
          </div>
        );
      }

      case "multiple-one":
        return <MultipleOptionsField field={field} type="one" onChange={handleMultipleChange} />;

      case "multiple-multiple":
        return <MultipleOptionsField field={field} type="multiple" onChange={handleMultipleChange} />;

      default:
        return <em>Geen type geselecteerd</em>;
    }
  };

  return (
    <div className="page-container">
      <div className="event-open-header-container">
        {eventForm &&
          eventForm.map((item, index) => (
            <div key={index}>
              {/* Bij welke activiteit hoort dit formulier? De titel is vaak de
                  standaard 'Nieuw formulier' (testronde 2026-10-28, Fatima B2).
                  Zelfde regel als 'Resultaat toevoegen' in de app. */}
              {activities && activities.map((activity) => (
                <p className="add-event-activity-title" key={activity.ID}>
                  Activiteit: <strong>{activity.Activity}</strong>
                </p>
              ))}
              <h1>{item.Title || title}</h1>
              <p>{item.Description || description}</p>
            </div>
          ))}
      </div>

      <div className="event-open-fields-container">
        {eventFields &&
          visibleEventFields(eventFields, { outputs, personaOn }).map((field) => (
            <div key={field.ID} className="event-open-field">
              <h2>{field.Title}</h2>
              <p>{field.Description}</p>
              {renderField(field)}
            </div>
          ))}
      </div>

      <div className="event-open-actions">
        {saved ? 
          <p>Bedankt voor het invullen. Je antwoorden zijn opgeslagen. Je mag het scherm nu sluiten.</p>
        : 
          <>
            {saveError && <p className="event-open-error" role="alert">{saveError}</p>}
            <button className="button" onClick={saveEvent} disabled={saving}>
              {saving ? "Bezig met versturen…" : "Verzenden"}
            </button>
          </>
        }
      </div>
    </div>
  );
};

export default EventOpen;
